import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  SUPPORTED_LANGUAGES,
  loadLocaleMessages,
  setDocumentDirectionByLocale,
  getTranslationStatus,
  generateLanguageMetaTags,
  type Language,
  type LocaleMetadata
} from '../i18n'

interface LocalizationState {
  isLoading: boolean;
  error: string | null;
  lastLoadedLocale: string | null;
  translationStatus: LocaleMetadata | null;
}

const state = ref<LocalizationState>({
  isLoading: false,
  error: null,
  lastLoadedLocale: null,
  translationStatus: null
})

export function useLocalization() {
  const { locale, t, d: datetime, n: number } = useI18n()
  const isChangingLocale = computed(() => state.value.isLoading)

  const currentLanguage = computed(() => {
    return SUPPORTED_LANGUAGES[locale.value] as Language | undefined
  })

  const isRTL = computed(() => {
    return currentLanguage.value?.dir === 'rtl'
  })

  const availableLanguages = computed(() => {
    return Object.values(SUPPORTED_LANGUAGES)
  })

  const numberSystem = computed(() => {
    return currentLanguage.value?.numberSystem || 'latn'
  })

  const translationStatus = computed(() => {
    return state.value.translationStatus
  })

  async function changeLanguage(newLocale: string) {
    if (state.value.isLoading || newLocale === locale.value) return

    try {
      state.value.isLoading = true
      state.value.error = null

      // Load messages for the new locale
      await loadLocaleMessages(newLocale)

      // Update the locale
      locale.value = newLocale
      setDocumentDirectionByLocale(newLocale)

      // Update translation status
      state.value.translationStatus = getTranslationStatus(newLocale)

      // Update meta tags for SEO
      const metaTags = generateLanguageMetaTags(newLocale)
      updateMetaTags(metaTags)

      // Save preference to localStorage
      localStorage.setItem('preferredLanguage', newLocale)
      state.value.lastLoadedLocale = newLocale

    } catch (error) {
      state.value.error = error instanceof Error ? error.message : 'Failed to change language'
      console.error('Failed to change language:', error)
    } finally {
      state.value.isLoading = false
    }
  }

  // Initialize language from localStorage or browser preference
  function initializeLanguage() {
    const savedLanguage = localStorage.getItem('preferredLanguage')
    const browserLanguage = navigator.language
    const defaultLanguage = 'en'

    const initialLanguage = savedLanguage ||
      (SUPPORTED_LANGUAGES[browserLanguage] ? browserLanguage :
       SUPPORTED_LANGUAGES[browserLanguage.split('-')[0]] ? browserLanguage.split('-')[0] :
       defaultLanguage)

    if (initialLanguage !== locale.value) {
      changeLanguage(initialLanguage)
    }
  }

  // Format date according to locale and calendar system
  function formatDate(date: Date | string, format: 'full' | 'long' | 'medium' | 'short' = 'medium'): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return datetime(dateObj, format)
  }

  // Format number according to locale and number system
  function formatNumber(num: number, options?: Intl.NumberFormatOptions): string {
    const opts = {
      ...options,
      numberingSystem: numberSystem.value
    }
    return number(num, opts)
  }

  // Format currency according to locale
  function formatCurrency(amount: number, currency = 'USD'): string {
    return number(amount, {
      style: 'currency',
      currency,
      numberingSystem: numberSystem.value
    })
  }

  // Update meta tags for SEO
  function updateMetaTags(metaTags: ReturnType<typeof generateLanguageMetaTags>) {
    // Update html attributes
    Object.entries(metaTags.htmlAttrs).forEach(([attr, value]) => {
      document.documentElement.setAttribute(attr, value)
    })

    // Update meta tags
    metaTags.meta.forEach(meta => {
      let tag = document.querySelector(`meta[http-equiv="${meta['http-equiv']}"]`)
      if (!tag) {
        tag = document.createElement('meta')
        tag.setAttribute('http-equiv', meta['http-equiv'])
        document.head.appendChild(tag)
      }
      tag.setAttribute('content', meta.content)
    })

    // Update alternate links
    metaTags.link.forEach(link => {
      let tag = document.querySelector(`link[hreflang="${link.hreflang}"]`)
      if (!tag) {
        tag = document.createElement('link')
        tag.setAttribute('rel', link.rel)
        tag.setAttribute('hreflang', link.hreflang)
        document.head.appendChild(tag)
      }
      tag.setAttribute('href', link.href)
    })
  }

  // Watch for errors to handle them appropriately
  watch(() => state.value.error, (error) => {
    if (error) {
      // You could emit an event or show a notification here
      console.error('Localization error:', error)
    }
  })

  return {
    currentLanguage,
    isRTL,
    isChangingLocale,
    availableLanguages,
    changeLanguage,
    initializeLanguage,
    formatDate,
    formatNumber,
    formatCurrency,
    numberSystem,
    translationStatus,
    error: computed(() => state.value.error),
    t
  }
}
